import {
  buildModoocarDetailUrl,
  buildRankCanonicalPath,
  buildRankOgImageUrl,
  getRankTabTitle,
  MODOOCAR_BASE_URL,
  MYCLE_HOME_URL,
  RANK_OG_IMAGE_SIZE,
} from '../_constants'

import type { IRankingList, TRankFuelType, TRankTab } from '../_types'
import type { Metadata } from 'next'

// [주의]: <를 이스케이프합니다. 빼면 데이터에 든 </script>가 스크립트를 일찍 닫아 XSS가 생깁니다.
export const safeJsonLd = (data: unknown): string =>
  JSON.stringify(data).replace(/</g, '\\u003c')

/**
 * [라이브러리]: Next 16은 한글·공백이 든 경로 파라미터를 generateMetadata에는 디코딩해서 넘기고, 페이지 컴포넌트에는 인코딩된 원본으로 넘깁니다.
 * 그래서 두 곳 모두 이 함수로 디코딩합니다. 이미 디코딩된 값에는 영향이 없습니다.
 */
export const decodeRouteParam = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

interface IRankSelection {
  tab: TRankTab
  segment: string
  fuelType: TRankFuelType
}

export const buildRankCanonicalUrl = ({
  tab,
  segment,
}: Pick<IRankSelection, 'tab' | 'segment'>): string =>
  `${MYCLE_HOME_URL}${buildRankCanonicalPath({ tab, segment })}`

/** [정책]: canonical은 색인 단위인 탭·차급만 반영하고 유종은 합칩니다. OG 이미지는 유종까지 반영해서 조합별 상위 차종으로 만듭니다. */
export const buildRankMetadata = ({
  tab,
  segment,
  fuelType,
}: IRankSelection): Metadata => {
  const isElectric = fuelType === '전기'
  const title = `${segment} ${getRankTabTitle(tab, isElectric)} | 마이클 모두의 차고`
  const description = `${segment} 차종을 오너들의 실측 데이터로 비교한 만족도·실연비·유류비 랭킹입니다. 실제 오너 데이터 기반 순위를 마이클에서 확인하세요.`
  const canonicalUrl = buildRankCanonicalUrl({ tab, segment })
  const ogImageUrl = buildRankOgImageUrl({ tab, segment, fuelType })

  return {
    title,
    description,
    alternates: { canonical: canonicalUrl },
    openGraph: {
      title,
      description,
      url: canonicalUrl,
      type: 'website',
      images: [{ url: ogImageUrl, ...RANK_OG_IMAGE_SIZE }],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: [ogImageUrl],
    },
  }
}

interface IRankJsonLdArgs {
  ranking: IRankingList
  tab: TRankTab
  segment: string
  isElectric: boolean
  canonicalUrl: string
}

export const buildRankItemListJsonLd = ({
  ranking,
  tab,
  segment,
  isElectric,
  canonicalUrl,
}: IRankJsonLdArgs) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `${segment} ${getRankTabTitle(tab, isElectric)}`,
  url: canonicalUrl,
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  numberOfItems: ranking.items.length,
  itemListElement: ranking.items.map(item => ({
    '@type': 'ListItem',
    position: item.rank,
    name: `${item.brandName} ${item.modelName}`,
    url: buildModoocarDetailUrl({
      brandName: item.brandName,
      modelName: item.modelName,
      fuelType: item.fuelType,
    }),
  })),
})

export const buildRankBreadcrumbJsonLd = ({
  tab,
  segment,
  isElectric,
  canonicalUrl,
}: Omit<IRankJsonLdArgs, 'ranking'>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '마이클', item: MYCLE_HOME_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: '모두의 차고',
      item: MODOOCAR_BASE_URL,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: `${segment} ${getRankTabTitle(tab, isElectric)}`,
      item: canonicalUrl,
    },
  ],
})
